import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import PriceRange from '../components/PriceRange';
import Skeleton from '../components/Skeleton';
import { STORAGE_BASE } from '../config';
import Modal from '../components/Modal';
import { Search as SearchIcon, X as XIcon, SlidersHorizontal } from 'lucide-react';
import { DefaultTemplateList, useListTplStore } from '../sections/ListTemplate';
/* vibefarsi listing anatomy: accordion filters + badge tags + product grid + empty state */
import { Accordion } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import { EmptyState } from '../components/ui/empty-state';
import { ProductGrid } from '../components/blocks/product-grid';
import { priceInfo } from '../lib/price';

const SORT_OPTIONS = [
  { value: 'newest', label: 'جدیدترین' },
  { value: 'price_asc', label: 'قیمت: کم به زیاد' },
  { value: 'price_desc', label: 'قیمت: زیاد به کم' },
  { value: 'name', label: 'نام' },
];

export default function AllProducts() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tplStore = useListTplStore();
  const hasCustomTpl = (tplStore.product || []).some(t => t.isDefault);
  const [sort, setSort] = useState(searchParams.get('sort') || 'newest');
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [attrs, setAttrs] = useState({});
  const [cats, setCats] = useState([]);
  const [catIds, setCatIds] = useState(() => {
    const multi = (searchParams.get('categories') || '').split(',').map(x => x.trim()).filter(Boolean);
    const single = searchParams.get('category');
    if (single) multi.unshift(single);
    return [...new Set(multi)];
  });
  const toggleCatId = (id) => setCatIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const [priceRange, setPriceRange] = useState({ lo: searchParams.get('min_price') || '', hi: searchParams.get('max_price') || '' });
  const [priceBounds, setPriceBounds] = useState({ min: 0, max: 500000 });
  const [mobileFilter, setMobileFilter] = useState(false);
  const [facets, setFacets] = useState({ attributes: [], price: {} });
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const filterKeyRef = useRef('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/filter-facets');
        setFacets(r.data);
        if (r.data.price) {
          setPriceBounds({ min: r.data.price.min, max: r.data.price.max });
          setPriceRange(prev => ({ lo: prev.lo !== '' ? prev.lo : r.data.price.min, hi: prev.hi !== '' ? prev.hi : r.data.price.max }));
        }
      } catch {}
      try {
        const c = await axios.get('/categories');
        setCats(c.data?.data || c.data || []);
      } catch {}
    })();
  }, []);

  const fetchProducts = useCallback(async (targetPage, replace) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    const params = { sort, search, page: targetPage, limit: 12 };
    if (priceRange.lo !== '') params.min_price = priceRange.lo;
    if (priceRange.hi !== '') params.max_price = priceRange.hi;
    if (catIds.length) params.category_ids = catIds.join(',');
    Object.entries(attrs).forEach(([k, v]) => { if (v) params[`attributes[${k}]`] = v; });
    try {
      const r = await axios.get('/products-filter', { params });
      setProducts(prev => (replace ? (r.data.data || []) : [...prev, ...(r.data.data || [])]));
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [sort, search, priceRange, attrs, catIds]);

  useEffect(() => {
    const key = JSON.stringify({ sort, search, priceRange, attrs, catIds });
    const isNewFilter = key !== filterKeyRef.current;
    filterKeyRef.current = key;
    if (isNewFilter) {
      setPage(1);
      fetchProducts(1, true);
    } else {
      fetchProducts(page, page === 1);
    }
  }, [sort, search, priceRange, attrs, catIds, page]);

  const toggleAttr = (name, value) => setAttrs(prev => ({ ...prev, [name]: prev[name] === value ? '' : value }));
  const clearFilters = () => {
    setAttrs({});
    setCatIds([]);
    setPriceRange({ lo: priceBounds.min, hi: priceBounds.max });
    setSearchInput('');
    setSearch('');
  };
  const imgOf = (p) => (p.media?.[0]?.path ? STORAGE_BASE + '/' + p.media[0].path : p.image_path ? STORAGE_BASE + '/' + p.image_path : undefined);
  const quickAdd = async (p) => {
    if (!localStorage.getItem('customer_token')) {
      window.dispatchEvent(new CustomEvent('open-login', { detail: { flow: 'order' } }));
      return;
    }
    try {
      await axios.post('/cart/add', { product_id: Number(p.id), qty: 1 }, { headers: { Authorization: 'Bearer ' + localStorage.getItem('customer_token') } });
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('به سبد خرید اضافه شد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    } catch (e) { toast.error(e.response?.data?.message || 'خطا در افزودن به سبد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); }
  };
  const activeAttrEntries = Object.entries(attrs).filter(([, v]) => v);
  const priceIsFiltered = priceRange.lo !== priceBounds.min || priceRange.hi !== priceBounds.max;
  const catNames = catIds.map(id => cats.find(c => String(c.id) === String(id))?.name).filter(Boolean);
  const hasActiveFilters = activeAttrEntries.length > 0 || priceIsFiltered || search || catIds.length > 0;

  const gridItems = products.map(p => {
    const pi = priceInfo(p);
    const hasAR = !!(p.customization_data?.ar_model || p.customization_data?.ar_image);
    const disc = pi.hasDiscount ? `٪${pi.pct.toLocaleString('fa-IR')}` : '';
    return {
      id: String(p.id),
      name: p.name,
      price: pi.price,
      original: pi.hasDiscount ? pi.base : undefined,
      badge: hasAR ? (disc ? `${disc} • AR` : 'پرو AR') : (disc || undefined),
      outOfStock: (p.stock || 0) < 1,
      image: imgOf(p) ? <img src={imgOf(p)} alt={p.name} loading="lazy" className="size-full object-cover" /> : undefined,
    };
  });

  const filterPanel = (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-extrabold">فیلترها</span>
        {hasActiveFilters && (
          <button type="button" onClick={clearFilters} className="text-xs text-muted-foreground underline hover:text-foreground">پاک کردن همه</button>
        )}
      </div>
      <Accordion
        defaultOpen={['cat', 'price']}
        items={[
          ...(cats.length ? [{
            id: 'cat', title: 'دسته‌بندی',
            content: (
              <div className="flex flex-wrap gap-1.5">
                {cats.map(c => {
                  const active = catIds.includes(String(c.id));
                  return (
                    <button key={c.id} type="button" aria-pressed={active} onClick={() => toggleCatId(String(c.id))}
                      className={`rounded-full border px-3 py-1.5 text-xs transition ${active ? 'border-primary bg-primary font-bold text-primary-foreground' : 'border-border text-foreground hover:border-muted-foreground'}`}>
                      {active ? '✓ ' : ''}{c.name}
                    </button>
                  );
                })}
              </div>
            ),
          }] : []),
          {
            id: 'price', title: 'محدوده قیمت (تومان)',
            content: <PriceRange min={priceBounds.min} max={priceBounds.max} value={priceRange} onChange={setPriceRange} />,
          },
          ...(facets.attributes || []).map(a => ({
            id: 'attr-' + a.name, title: a.name,
            content: (
              <div className="flex flex-wrap gap-1.5">
                {a.values.map(v => {
                  const active = attrs[a.name] === v;
                  return (
                    <button key={v} type="button" aria-pressed={active} onClick={() => toggleAttr(a.name, v)}
                      className={`rounded-full border px-3 py-1.5 text-xs transition ${active ? 'border-primary bg-primary font-bold text-primary-foreground' : 'border-border text-foreground hover:border-muted-foreground'}`}>
                      {v}
                    </button>
                  );
                })}
              </div>
            ),
          })),
        ]}
      />
    </div>
  );

  return (
    <div className="w-full px-4 py-8 sm:px-6" dir="rtl">
      <header className="mb-6">
        <h1 className="text-2xl font-extrabold sm:text-3xl">همه محصولات</h1>
        <p className="mt-1 text-sm text-muted-foreground">جستجو و فیلتر بر اساس دسته، ویژگی و محدوده قیمت</p>
      </header>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="sticky top-4 hidden rounded-2xl border border-border bg-card p-5 lg:block">
          {filterPanel}
        </aside>

        <div className="min-w-0">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <SearchIcon size={18} className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="جستجو در محصولات..."
                aria-label="جستجو در محصولات"
                className="w-full rounded-xl border border-border bg-card py-2.5 pe-10 ps-3 text-sm outline-none placeholder:text-muted-foreground focus:border-primary"
              />
              {searchInput && (
                <button type="button" onClick={() => setSearchInput('')} aria-label="پاک کردن جستجو"
                  className="absolute start-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground">
                  <XIcon size={15} />
                </button>
              )}
            </div>
            <Button variant="outline" size="icon" className="relative shrink-0 lg:hidden" onClick={() => setMobileFilter(true)} aria-label="نمایش فیلترها">
              <SlidersHorizontal size={18} />
              {hasActiveFilters && <span className="absolute end-1.5 top-1.5 size-2 rounded-full bg-primary" />}
            </Button>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {search && (
                <Badge variant="secondary" className="gap-1">«{search}»
                  <button type="button" onClick={() => setSearchInput('')} aria-label="حذف فیلتر جستجو" className="flex"><XIcon size={12} /></button>
                </Badge>
              )}
              {catNames.map(n => (
                <Badge variant="secondary" className="gap-1" key={n}>{n}
                  <button type="button" onClick={() => setCatIds([])} aria-label="حذف فیلتر دسته‌ها" className="flex"><XIcon size={12} /></button>
                </Badge>
              ))}
              {activeAttrEntries.map(([k, v]) => (
                <Badge variant="secondary" className="gap-1" key={k}>{k}: {v}
                  <button type="button" onClick={() => toggleAttr(k, v)} aria-label={`حذف فیلتر ${k}`} className="flex"><XIcon size={12} /></button>
                </Badge>
              ))}
              {priceIsFiltered && (
                <Badge variant="secondary" className="gap-1">
                  قیمت: {Number(priceRange.lo).toLocaleString('fa-IR')} تا {Number(priceRange.hi).toLocaleString('fa-IR')}
                  <button type="button" onClick={() => setPriceRange({ lo: priceBounds.min, hi: priceBounds.max })} aria-label="حذف فیلتر قیمت" className="flex"><XIcon size={12} /></button>
                </Badge>
              )}
            </div>
          )}

          <Modal open={mobileFilter} onClose={() => setMobileFilter(false)} title="فیلترها" contentHeight="640px">
            {filterPanel}
          </Modal>

          <div className="mb-4 mt-4 flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">{total.toLocaleString('fa-IR')} محصول</span>
            <Select
              className="w-44"
              value={sort}
              onChange={e => setSort(e.target.value)}
              options={SORT_OPTIONS}
              aria-label="مرتب‌سازی"
            />
          </div>

          {loading ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-2xl border border-border bg-card">
                  <Skeleton height={180} />
                  <div className="space-y-2 p-3"><Skeleton height={16} /><Skeleton height={14} width="50%" /></div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <EmptyState
              title="محصولی پیدا نشد"
              description="با این فیلترها چیزی نیست؛ فیلترها را کم کن یا عبارت دیگری جستجو کن."
              action={hasActiveFilters ? <Button variant="outline" onClick={clearFilters}>پاک کردن فیلترها</Button> : undefined}
            />
          ) : hasCustomTpl ? (
            <DefaultTemplateList kind="product" items={products} fallbackTpl={{ layout: 'grid' }} />
          ) : (
            <div className="[&_section]:p-0 [&_section]:py-2 [&_ul]:max-w-none">
              <ProductGrid products={gridItems} onAdd={quickAdd} href={(p) => `/products/${p.id}`} />
            </div>
          )}

          {!loading && products.length < total && (
            <div className="mt-6 text-center">
              <Button variant="outline" disabled={loadingMore} onClick={() => setPage(p => p + 1)}>
                {loadingMore ? 'در حال بارگذاری...' : 'نمایش محصولات بیشتر'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
